import { supabase } from '../lib/supabase';
import { Player } from '../types';
import { SupabaseError } from '../lib/supabase';

export const getPlayers = async (): Promise<Player[]> => {
  try {
    const { data: players, error } = await supabase
      .from('players')
      .select(`
        *,
        squads (
          name,
          display_name
        )
      `)
      .order('name');

    if (error) {
      console.error('Error fetching players:', error);
      throw error;
    }

    if (!players) {
      return [];
    }

    // Transform the data to match our Player type
    return players.map(player => ({
      id: player.id,
      name: player.name,
      fatherName: player.father_name,
      motherName: player.mother_name,
      fatherPhone: player.father_phone,
      motherPhone: player.mother_phone,
      fatherEmail: player.father_email,
      motherEmail: player.mother_email,
      dateOfBirth: player.date_of_birth,
      address: player.address,
      squad: player.squads.display_name,
      subscriptionStatus: player.subscription_status,
      subscriptionValidTill: player.subscription_valid_till,
      scholarshipPercentage: player.scholarship_percentage,
      aiffPlayerId: player.aiff_player_id
    }));
  } catch (error) {
    console.error('Error in getPlayers:', error);
    throw error;
  }
};

export const getPlayerById = async (id: string): Promise<Player | null> => {
  try {
    const { data: player, error } = await supabase
      .from('players')
      .select(`
        *,
        squads (
          name,
          display_name
        )
      `)
      .eq('id', id)
      .single();

    if (error) {
      console.error('Error fetching player by ID:', error);
      throw error;
    }

    if (!player) {
      return null;
    }

    return {
      id: player.id,
      name: player.name,
      fatherName: player.father_name,
      motherName: player.mother_name,
      fatherPhone: player.father_phone,
      motherPhone: player.mother_phone,
      fatherEmail: player.father_email,
      motherEmail: player.mother_email,
      dateOfBirth: player.date_of_birth,
      address: player.address,
      squad: player.squads.display_name,
      subscriptionStatus: player.subscription_status,
      subscriptionValidTill: player.subscription_valid_till,
      scholarshipPercentage: player.scholarship_percentage,
      aiffPlayerId: player.aiff_player_id
    };
  } catch (error) {
    console.error('Error in getPlayerById:', error);
    throw error;
  }
};

export const createPlayer = async (player: Omit<Player, 'id'>): Promise<Player> => {
  try {
    // Get squad ID first
    const { data: squadData, error: squadError } = await supabase
      .from('squads')
      .select('id')
      .eq('display_name', player.squad)
      .single();

    if (squadError) {
      console.error('Error getting squad ID:', squadError);
      throw squadError;
    }

    if (!squadData) {
      throw new Error('Squad not found');
    }

    // Transform the data to match the database schema
    const { data: newPlayer, error } = await supabase
      .from('players')
      .insert({
        name: player.name,
        father_name: player.fatherName,
        mother_name: player.motherName,
        father_phone: player.fatherPhone,
        mother_phone: player.motherPhone,
        father_email: player.fatherEmail,
        mother_email: player.motherEmail,
        date_of_birth: player.dateOfBirth,
        address: player.address,
        squad_id: squadData.id,
        subscription_status: player.subscriptionStatus,
        subscription_valid_till: player.subscriptionValidTill,
        scholarship_percentage: player.scholarshipPercentage,
        aiff_player_id: player.aiffPlayerId
      })
      .select(`
        *,
        squads (
          name,
          display_name
        )
      `)
      .single();

    if (error) {
      console.error('Error creating player:', error);
      throw error;
    }

    if (!newPlayer) {
      throw new Error('No player data returned after creation');
    }

    // Transform and return the new player
    return {
      id: newPlayer.id,
      name: newPlayer.name,
      fatherName: newPlayer.father_name,
      motherName: newPlayer.mother_name,
      fatherPhone: newPlayer.father_phone,
      motherPhone: newPlayer.mother_phone,
      fatherEmail: newPlayer.father_email,
      motherEmail: newPlayer.mother_email,
      dateOfBirth: newPlayer.date_of_birth,
      address: newPlayer.address,
      squad: newPlayer.squads.display_name,
      subscriptionStatus: newPlayer.subscription_status,
      subscriptionValidTill: newPlayer.subscription_valid_till,
      scholarshipPercentage: newPlayer.scholarship_percentage,
      aiffPlayerId: newPlayer.aiff_player_id
    };
  } catch (error) {
    console.error('Error in createPlayer:', error);
    throw error;
  }
};

export const updatePlayer = async (id: string, player: Omit<Player, 'id'>): Promise<Player> => {
  try {
    // Get squad ID first
    const { data: squadData, error: squadError } = await supabase
      .from('squads')
      .select('id')
      .eq('display_name', player.squad)
      .single();

    if (squadError) {
      console.error('Error getting squad ID:', squadError);
      throw squadError;
    }

    if (!squadData) {
      throw new Error('Squad not found');
    }

    // Transform the data to match the database schema
    const { data: updatedPlayer, error } = await supabase
      .from('players')
      .update({
        name: player.name,
        father_name: player.fatherName,
        mother_name: player.motherName,
        father_phone: player.fatherPhone,
        mother_phone: player.motherPhone,
        father_email: player.fatherEmail,
        mother_email: player.motherEmail,
        date_of_birth: player.dateOfBirth,
        address: player.address,
        squad_id: squadData.id,
        subscription_status: player.subscriptionStatus,
        subscription_valid_till: player.subscriptionValidTill,
        scholarship_percentage: player.scholarshipPercentage,
        aiff_player_id: player.aiffPlayerId
      })
      .eq('id', id)
      .select(`
        *,
        squads (
          name,
          display_name
        )
      `)
      .single();

    if (error) {
      console.error('Error updating player:', error);
      throw error;
    }

    if (!updatedPlayer) {
      throw new Error('No player data returned after update');
    }

    // Transform and return the updated player
    return {
      id: updatedPlayer.id,
      name: updatedPlayer.name,
      fatherName: updatedPlayer.father_name,
      motherName: updatedPlayer.mother_name,
      fatherPhone: updatedPlayer.father_phone,
      motherPhone: updatedPlayer.mother_phone,
      fatherEmail: updatedPlayer.father_email,
      motherEmail: updatedPlayer.mother_email,
      dateOfBirth: updatedPlayer.date_of_birth,
      address: updatedPlayer.address,
      squad: updatedPlayer.squads.display_name,
      subscriptionStatus: updatedPlayer.subscription_status,
      subscriptionValidTill: updatedPlayer.subscription_valid_till,
      scholarshipPercentage: updatedPlayer.scholarship_percentage,
      aiffPlayerId: updatedPlayer.aiff_player_id
    };
  } catch (error) {
    console.error('Error in updatePlayer:', error);
    throw error;
  }
};

export const deletePlayer = async (id: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from('players')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting player:', error);
      throw error;
    }
  } catch (error) {
    console.error('Error in deletePlayer:', error);
    throw error;
  }
};

export const getPlayersBySquad = async (squad: string): Promise<Player[]> => {
  try {
    // Get squad ID first
    const { data: squadData, error: squadError } = await supabase
      .from('squads')
      .select('id')
      .eq('name', squad)
      .single();

    if (squadError) {
      console.error('Error getting squad ID:', squadError);
      throw squadError;
    }

    if (!squadData) {
      throw new Error('Squad not found');
    }

    const { data: players, error } = await supabase
      .from('players')
      .select(`
        *,
        squads (
          name,
          display_name
        )
      `)
      .eq('squad_id', squadData.id)
      .eq('subscription_status', 'Active')
      .order('name');

    if (error) {
      console.error('Error fetching players by squad:', error);
      throw error;
    }

    if (!players) {
      return [];
    }

    // Transform and return the players
    return players.map(player => ({
      id: player.id,
      name: player.name,
      fatherName: player.father_name,
      motherName: player.mother_name,
      fatherPhone: player.father_phone,
      motherPhone: player.mother_phone,
      fatherEmail: player.father_email,
      motherEmail: player.mother_email,
      dateOfBirth: player.date_of_birth,
      address: player.address,
      squad: player.squads.display_name,
      subscriptionStatus: player.subscription_status,
      subscriptionValidTill: player.subscription_valid_till,
      scholarshipPercentage: player.scholarship_percentage,
      aiffPlayerId: player.aiff_player_id
    }));
  } catch (error) {
    console.error('Error in getPlayersBySquad:', error);
    throw error;
  }
};